import React from 'react';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  EditOutlined as EditIcon,
  MenuBook as BookIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useBooksStore } from '../../store/books';
import type { Book, BookCreatePayload, BookStatus } from '../../books/types';
import { formatLongDate, getVisibleBooks } from '../../books/utils';
import BookEditorDialog from './BookEditorDialog';
import BookProgressSlider from './BookProgressSlider';

interface BooksTrackerPanelProps {
  title?: string;
  showCompletedToggle?: boolean;
  showCompleted?: boolean;
  onShowCompletedChange?: (nextValue: boolean) => void;
  emptyMessage?: string;
  variant?: 'widget' | 'page';
}

const paceChipColor = (status: Book['pace_status']) => {
  switch (status) {
    case 'ahead':
      return 'success';
    case 'behind':
      return 'error';
    default:
      return 'warning';
  }
};

const statusChipColor = (status: BookStatus) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'paused':
      return 'default';
    default:
      return 'primary';
  }
};

const BookCard: React.FC<{
  book: Book;
  onEdit: (book: Book) => void;
  onProgressCommit: (book: Book, position: number) => void;
  onMarkComplete: (book: Book) => void;
  compact?: boolean;
}> = ({ book, onEdit, onProgressCommit, onMarkComplete, compact = false }) => (
  <Box
    sx={{
      p: compact ? 1.75 : 2,
      borderRadius: 3,
      bgcolor: 'action.hover',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: book.color,
                boxShadow: `0 0 0 3px ${alpha(book.color, 0.16)}`,
                flexShrink: 0,
              }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              {book.title}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {book.author || 'Unknown author'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip size="small" variant="outlined" label={`Due ${formatLongDate(book.due_date)}`} />
          <Chip size="small" color={statusChipColor(book.status)} label={book.status} />
          <Chip size="small" color={paceChipColor(book.pace_status)} variant="outlined" label={book.pace_status} />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {book.intro_pages > 0
            ? `${book.intro_pages} intro page${book.intro_pages === 1 ? '' : 's'} before page 1`
            : 'No intro section'}
        </Typography>
        {book.completed_at ? (
          <Typography variant="caption" color="text.secondary">
            Completed {formatLongDate(book.completed_at)}
          </Typography>
        ) : null}
      </Stack>

      {book.status !== 'completed' ? (
        <BookProgressSlider book={book} onCommit={(position) => onProgressCommit(book, position)} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Finished at p.{book.total_pages}
        </Typography>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {book.status !== 'completed' ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckIcon fontSize="small" />}
            onClick={() => onMarkComplete(book)}
          >
            Finish
          </Button>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon fontSize="small" />}
          onClick={() => onEdit(book)}
        >
          Edit
        </Button>
      </Stack>
    </Stack>
  </Box>
);

const BooksTrackerPanel: React.FC<BooksTrackerPanelProps> = ({
  title = 'Books',
  showCompletedToggle = false,
  showCompleted: controlledShowCompleted,
  onShowCompletedChange,
  emptyMessage = 'No books yet',
  variant = 'widget',
}) => {
  const { books, loading, error, fetchBooks, addBook, updateBook, clearError } = useBooksStore();
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingBook, setEditingBook] = React.useState<Book | null>(null);
  const [internalShowCompleted, setInternalShowCompleted] = React.useState(false);
  const showCompleted = controlledShowCompleted ?? internalShowCompleted;

  React.useEffect(() => {
    void fetchBooks();
  }, [fetchBooks]);

  const visibleBooks = React.useMemo(() => getVisibleBooks(books, showCompleted), [books, showCompleted]);

  const sortedBooks = React.useMemo(
    () =>
      [...visibleBooks].sort((left, right) => {
        const statusOrder: Record<BookStatus, number> = { reading: 0, paused: 1, completed: 2 };
        if (statusOrder[left.status] !== statusOrder[right.status]) {
          return statusOrder[left.status] - statusOrder[right.status];
        }
        return left.due_date.localeCompare(right.due_date);
      }),
    [visibleBooks]
  );

  const openCreateDialog = () => {
    setEditingBook(null);
    setEditorOpen(true);
    clearError();
  };

  const openEditDialog = (book: Book) => {
    setEditingBook(book);
    setEditorOpen(true);
    clearError();
  };

  const closeDialog = () => {
    setEditorOpen(false);
    setEditingBook(null);
  };

  const handleSave = async (payload: BookCreatePayload) => {
    if (editingBook) {
      const updated = await updateBook(editingBook.id, payload);
      if (updated) {
        closeDialog();
      }
    } else {
      const created = await addBook(payload);
      if (created) {
        closeDialog();
      }
    }
  };

  const handleMarkComplete = (book: Book) => {
    void updateBook(book.id, { status: 'completed' });
  };

  const handleProgressCommit = (book: Book, position: number) => {
    void updateBook(book.id, { current_position: position });
  };

  const updateShowCompleted = (nextValue: boolean) => {
    onShowCompletedChange?.(nextValue);
    if (controlledShowCompleted === undefined) {
      setInternalShowCompleted(nextValue);
    }
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardHeader
          avatar={<BookIcon />}
          title={title}
          subheader={variant === 'page' ? 'Reading and paused books stay visible by default.' : undefined}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Add book">
                <IconButton aria-label="Add book" onClick={openCreateDialog}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh books">
                <IconButton aria-label="Refresh books" onClick={() => void fetchBooks()} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          }
          sx={{ pb: 0 }}
        />

        <CardContent
          sx={{
            pt: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            flex: 1,
            minHeight: 0,
            overflowY: variant === 'widget' ? 'auto' : 'visible',
          }}
        >
          {showCompletedToggle ? (
            <FormControlLabel
              control={
                <Switch
                  checked={showCompleted}
                  onChange={(event) => updateShowCompleted(event.target.checked)}
                />
              }
              label="Show completed books"
            />
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={variant === 'widget' ? 180 : 200} />
            ))
          ) : sortedBooks.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {emptyMessage}
            </Typography>
          ) : (
            sortedBooks.map((book) => (
              <BookCard
                key={book.id}
                book={book}
                onEdit={openEditDialog}
                onMarkComplete={handleMarkComplete}
                onProgressCommit={handleProgressCommit}
                compact={variant === 'widget'}
              />
            ))
          )}
        </CardContent>
      </Card>

      <BookEditorDialog
        open={editorOpen}
        book={editingBook}
        onClose={closeDialog}
        onSubmit={handleSave}
      />
    </>
  );
};

export default BooksTrackerPanel;
